﻿import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import classNames from 'classnames';

import theme from "./FormItem.module.sass";

const formItemDefaultPropRegisterOptions = {};
const FormItem = ({
  name,
  fill = true,
  inlineLabel = false,
  inlineLabelClassName,
  reverseLabel = false,
  label,
  input,
  registerOptions = formItemDefaultPropRegisterOptions,
  onChange,
  ...others
}) => {
  const { formState: { errors }, control } = useFormContext();
  const inputError = errors[name];
  // A Form.Item has TWO defaults, and for an item with its own `onChange` they are in
  // different shapes. The input's defaultValue is what the person sees before acting;
  // the form's default is what is submitted if they never act. A transforming item
  // stores what its handler returns (a survey answer is [{ question, text }]), so the
  // input's default is the wrong shape for the form and the form's is the wrong shape
  // for the input. From 5.1.2 to 5.1.77 they were mixed: the check-in slider's 50 was
  // handed to react-hook-form as the ANSWER, so an untouched required slider passed
  // validation and the bare number reached the API, which crashed on it; and the form's
  // array was handed to the slider as ITS default, which MUI reads as a range slider,
  // so a slider with a saved answer emitted a one-element list when moved. A production
  // client could not submit her check-in for three days (CLAUDE.md, class 14). The
  // transforming branch therefore takes the form's default from Form.Item alone,
  // exactly as origin/master did (`others.defaultValue ?? null`), and leaves the input's
  // own defaultValue untouched. The plain, controlled branch keeps the input's default
  // as the form's, because there the stored value really is the input's own string.
  const { defaultValue: formDefaultValue, ...inputOthers } = others;
  return (
    <div
      className={classNames(
        inlineLabel
          ? classNames(inlineLabelClassName, theme.HorizontalFormItem)
          : theme.VerticalFormItem,
        {
          [theme.FormItemFill]: fill,
          [theme.ReverseFormItem]: reverseLabel,
        },
      )}
    >
      <div
        className={classNames(
          inlineLabel ? theme.InlineLabel : theme.VerticalLabel,
          {
            [theme.ReverseLabel]: reverseLabel,
          },
        )}
      >
        {registerOptions.required && (
          <span className={theme.RequiredMark}>*</span>
        )}
        {label}
      </div>
      <Controller
        name={name}
        control={control}
        rules={registerOptions}
        render={({ field: { onChange: rhfOnChange, onBlur, value, ref } }) => {
          const typeName = input.type?.displayName || input.type?.name || input.type?.render?.name;
          const isCheckable = typeName === 'Radio' || typeName === 'Checkbox' || input.props?.type === 'radio' || input.props?.type === 'checkbox';
          // A Form.Item with its own `onChange` TRANSFORMS what it stores: the survey
          // questions turn a keystroke into [{ question, text }], which is the shape the
          // API wants and is not a value any input can display. Feeding it back made
          // every free-text answer in the check-in and the health questionnaire render
          // as "[object Object]" from the first character typed, and stored that string
          // as the client's answer. Those inputs are uncontrolled and take their initial
          // content from defaultValue, exactly as they do on origin/master, which never
          // passed value here at all.
          const additionalProps = isCheckable
            ? { checked: input.props.value !== undefined ? String(value) === String(input.props.value) : Boolean(value) }
            : (onChange ? {} : { value });

          return React.cloneElement(input, {
            ...input.props,
            onChange: (...args) => {
              if (onChange) {
                const data = onChange(...args);
                rhfOnChange(data);
              } else {
                rhfOnChange(...args);
              }
            },
            onBlur,
            ref,
            error: inputError?.message,
            ...(onChange ? inputOthers : others),
            ...additionalProps,
          });
        }}
        {...others}
        defaultValue={
          onChange
            ? formDefaultValue ?? null
            : formDefaultValue ?? input.props.defaultValue ?? ''
        }
      />
    </div>
  );
};

FormItem.propTypes = {
  // Common Props
  name: PropTypes.string.isRequired,
  fill: PropTypes.bool,
  inlineLabel: PropTypes.bool,
  inlineLabelClassName: PropTypes.string,
  reverseLabel: PropTypes.bool,
  label: PropTypes.node,
  input: PropTypes.node.isRequired,
  registerOptions: PropTypes.object,
  // Uncontrolled Props
  onChange: PropTypes.func,
};

export default FormItem;
